import { useState, useEffect } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { API } from "@/App";
import axios from "axios";
import { Play, Clock, Star, Leaf, Smile, Heart, Wind, Waves } from "lucide-react";

export default function MindSpa() {
  const [sessions, setSession] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");

  const categories = [
    { id: "all", label: "All Sessions" },
    { id: "meditation", label: "Meditation" },
    { id: "laughter", label: "Laughter Therapy" },
    { id: "breathing", label: "Breathing" },
    { id: "relaxation", label: "Relaxation" },
  ];

  const mindSpaSessions = [
    {
      id: 1,
      title: "Morning Gratitude Meditation",
      description: "Start your day with gratitude to reset your nervous system",
      duration: "15 min",
      category: "meditation",
      image: "https://images.unsplash.com/photo-1659177121700-bc9c36da8a40?w=600",
      icon: Leaf,
      coins: 10
    },
    {
      id: 2,
      title: "The Laughing Therapist Session",
      description: "Laughter therapy to improve mental health and emotional balance",
      duration: "20 min",
      category: "laughter",
      image: "https://images.unsplash.com/photo-1694299352873-0c29d862e87a?w=600",
      icon: Smile,
      coins: 15
    },
    {
      id: 3,
      title: "Deep Breathing for Calm",
      description: "Evidence-based breathing techniques for stress relief",
      duration: "10 min",
      category: "breathing",
      image: "https://images.unsplash.com/photo-1760542758210-7f1be42027dc?w=600",
      icon: Wind,
      coins: 5
    },
    {
      id: 4,
      title: "Emotional Wellness Reset",
      description: "Guided session for emotional regulation and balance",
      duration: "25 min",
      category: "meditation",
      image: "https://images.pexels.com/photos/12994733/pexels-photo-12994733.png?auto=compress&cs=tinysrgb&w=600",
      icon: Heart,
      coins: 20
    },
    {
      id: 5,
      title: "Stress Response Adaptation",
      description: "Learn adaptive techniques for managing life stressors",
      duration: "30 min",
      category: "relaxation",
      image: "https://images.pexels.com/photos/34993558/pexels-photo-34993558.jpeg?auto=compress&cs=tinysrgb&w=600",
      icon: Waves,
      coins: 25
    },
    {
      id: 6,
      title: "Faith-Centered Cognition",
      description: "Align your mindset with faith-centered wellness practices",
      duration: "20 min",
      category: "meditation",
      image: "https://images.unsplash.com/photo-1615134732800-ca7ef7a3388c?w=600",
      icon: Star,
      coins: 15
    }
  ];

  const filteredSessions = activeCategory === "all" 
    ? mindSpaSessions 
    : mindSpaSessions.filter(s => s.category === activeCategory);

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 lg:p-12 space-y-8 animate-fade-in" data-testid="mind-spa-page">
        {/* Header */}
        <div>
          <p className="overline-text mb-2">Virtual Wellness Experience</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-light text-spa-moss mb-3">
            Mind-SPA Sessions
          </h1>
          <p className="text-spa-text-secondary max-w-2xl">
            Experience rejuvenating virtual spa sessions designed to enhance your mental 
            and emotional well-being through meditation, laughter therapy, and adaptive stress responses.
          </p>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeCategory === cat.id
                  ? 'bg-spa-terracotta text-white'
                  : 'bg-spa-muted text-spa-text-secondary hover:bg-spa-border'
              }`}
              data-testid={`category-${cat.id}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Sessions Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSessions.map((session) => (
            <div 
              key={session.id} 
              className="spa-card overflow-hidden group cursor-pointer"
              data-testid={`session-card-${session.id}`}
            >
              <div className="relative h-48 -mx-6 -mt-6 mb-4 overflow-hidden">
                <img 
                  src={session.image} 
                  alt={session.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center gap-2 text-white/90 text-sm">
                    <Clock className="w-4 h-4" />
                    <span>{session.duration}</span>
                    <span className="ml-auto bg-spa-terracotta px-2 py-0.5 rounded-full text-xs">
                      +{session.coins} coins
                    </span>
                  </div>
                </div>
                <button className="absolute top-4 right-4 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-colors">
                  <Play className="w-5 h-5 text-spa-terracotta ml-0.5" fill="currentColor" />
                </button>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-spa-sage/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <session.icon className="w-5 h-5 text-spa-moss" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-serif text-lg text-spa-moss mb-1">{session.title}</h3>
                  <p className="text-sm text-spa-text-secondary line-clamp-2">{session.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Featured Session */}
        <div className="spa-card bg-gradient-to-br from-spa-sage/20 to-spa-muted overflow-hidden">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div>
              <p className="overline-text mb-2">Featured Experience</p>
              <h2 className="font-serif text-2xl text-spa-moss mb-3">
                MindSET REset reTREAT
              </h2>
              <p className="text-spa-text-secondary mb-4">
                RAPS-SMAHoo ME.WELL Mind-SPA Sof.B & WELL.b exPE REtreats - Nurture and maintain 
                your daily adaptive stress responses aligned with WHO mental health principles.
              </p>
              <button className="spa-button-primary flex items-center gap-2">
                <Play className="w-4 h-4" /> Start Experience
              </button>
            </div>
            <div className="h-48 md:h-64 rounded-2xl overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1601996759374-f9aa091f3a25?w=600"
                alt="Retreat"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
